<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h3>{{ modalTitle }}</h3>
        <button 
          class="close-button" 
          @click="handleClose" 
          v-if="!loading"
        >&times;</button>
      </div>
      
      <div class="modal-body">
        <!-- Payment Success State -->
        <div v-if="paymentStatus === 'success'" class="success-state">
          <div class="success-icon">
            <i class="fas fa-check"></i>
          </div>
          <h4>Payment Successful!</h4>
          <p>Thank you for purchasing the Content Commander Challenge! You'll see a charge from AI CAPTAINS LLC.</p>
          <p>You'll receive a confirmation email with your API key shortly, and you can get started!</p>
          <p class="credit-note">One more thing: your purchase counts as credit toward AI Captains Academy membership whenever you're ready to go deeper. Just email <a href="mailto:ahoy@aicaptains.ai">ahoy@aicaptains.ai</a> and we'll apply it.</p>
          <div class="success-buttons">
            <a 
              href="https://github.com/jordanurbs/terminal-navigation-toolkit.git" 
              target="_blank" 
              rel="noopener noreferrer" 
              class="download-link"
            >
              <i class="fab fa-github"></i>
              Download Repository
            </a>
            <a 
              href="https://toolkit.ai-captains.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              class="download-link"
            >
              <i class="fas fa-globe"></i>
              Work Online Instead
            </a>
          </div>
        </div>

        <!-- Payment Failed State -->
        <div v-else-if="paymentStatus === 'failed'" class="failed-state">
          <div class="failed-icon">
            <i class="fas fa-times"></i>
          </div>
          <h4>Payment Failed</h4>
          <p>{{ error }}</p>
          <div class="button-group">
            <button class="secondary-button" @click="$emit('close')">
              <i class="fas fa-times"></i> Cancel
            </button>
            <button class="cta-button" @click="resetPayment">
              <i class="fas fa-redo"></i> Try Again
            </button>
          </div>
        </div>

        <!-- Default Payment Form State -->
        <div v-else>
          <div class="order-summary">
            <div class="product-header">
          
              <h4>Order Summary</h4>
            </div>
            <div class="product-details">
              <span>
                <i class="fas fa-terminal product-icon"></i>
                Terminal Navigation Toolkit
              </span>
              <span>${{ formattedPrice }}</span>
            </div>
            <div class="features-list">
              <div class="feature-item">
                <i class="fas fa-key"></i>
                <span>Lifetime Personal API Key</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-sync-alt"></i>
                <span>Progress Sync & Captain's Log</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-book"></i>
                <span>Command Line Cheat Sheet</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-plus-circle"></i>
                <span>And More!</span>
              </div>
            </div>
            <div class="total">
              <strong><i class="fas fa-tag"></i> Total:</strong>
              <strong>${{ formattedPrice }}</strong>
            </div>
          </div>

          <div class="payment-form">
            <form @submit.prevent="handleSubmit">
              <div class="form-group">
                <label for="email">
                  <i class="fas fa-envelope"></i> Email address
                </label>
                <input 
                  type="email" 
                  id="email" 
                  v-model="email" 
                  class="form-input"
                  required
                  placeholder="Enter your email"
                  :disabled="loading"
                />
                <small class="form-help">
                  <i class="fas fa-info-circle"></i>
                  We'll send your API key to this email
                </small>
              </div>
              <div id="card-element"></div>
              <div id="card-errors" role="alert" v-if="error" class="error-message">
                <i class="fas fa-exclamation-circle"></i> {{ error }}
              </div>
              <button 
                type="submit" 
                class="pay-button" 
                :disabled="loading || !stripe"
              >
                <span v-if="loading">
                  <span class="spinner"></span>
                  Processing...
                </span>
                <span v-else>
                  <i class="fas fa-lock"></i> Pay Now
                </span>
              </button>
              <div class="stripe-powered">
                <img src="/images/stripe-white.svg" alt="Powered by Stripe" />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';

export default {
  name: 'CheckoutModal',
  props: {
    show: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      stripe: null,
      elements: null,
      card: null,
      error: null,
      loading: false,
      paymentStatus: null, // null, 'success', or 'failed'
      email: '' // Add email field
    }
  },
  computed: {
    modalTitle() {
      if (this.paymentStatus === 'success') return 'Payment Successful';
      if (this.paymentStatus === 'failed') return 'Payment Failed';
      return 'Complete Your Purchase';
    },
    price() {
      // Get price from environment variable with a fallback
      return parseInt(import.meta.env.VITE_CONTENT_COMMANDER_PRICE) || 2900;
    },
    formattedPrice() {
      // Format the price as dollars and cents
      return (this.price / 100).toFixed(2);
    }
  },
  methods: {
    async initializeStripe() {
      try {
        // Get the Stripe key from environment
        const stripeKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
        
        if (!stripeKey) {
          console.error('Stripe key is missing');
          throw new Error('Payment system configuration error');
        }

        // Log key information (safely)
        console.log('Stripe configuration:', {
          keyType: stripeKey.startsWith('pk_test_') ? 'test' : 'live',
          keyPrefix: stripeKey.substring(0, 7),
          keyLength: stripeKey.length,
          environment: import.meta.env.MODE
        });

        // Initialize Stripe
        this.stripe = await loadStripe(stripeKey);
        
        if (!this.stripe) {
          throw new Error('Failed to initialize Stripe');
        }

        // Create Elements instance with explicit appearance
        this.elements = this.stripe.elements({
          locale: 'en',
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#007bff',
              colorBackground: '#ffffff',
              colorText: '#32325d',
              colorDanger: '#dc3545',
              fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
              spacingUnit: '4px',
              borderRadius: '4px'
            }
          }
        });
        
        // Use nextTick to ensure the DOM element exists
        this.$nextTick(() => {
          if (!this.card) {
            this.card = this.elements.create('card', {
              style: {
                base: {
                  fontSize: '16px',
                  color: '#32325d',
                  fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                  '::placeholder': {
                    color: '#aab7c4'
                  },
                  iconColor: '#666ee8',
                  ':-webkit-autofill': {
                    color: '#32325d',
                  },
                },
                invalid: {
                  color: '#fa755a',
                  iconColor: '#fa755a'
                }
              },
              hidePostalCode: true
            });
            
            // Mount the card element
            const cardElement = document.getElementById('card-element');
            if (cardElement) {
              console.log('Mounting card element...');
              this.card.mount('#card-element');
              
              this.card.on('change', (event) => {
                console.log('Card element change:', event);
                if (event.error) {
                  this.error = event.error.message;
                } else {
                  this.error = null;
                }
              });
            } else {
              console.error('Card element container not found');
              this.error = 'Payment form initialization failed. Please try again.';
            }
          }
        });
      } catch (err) {
        console.error('Error initializing Stripe:', err);
        this.error = 'Failed to initialize payment form. Please try again.';
      }
    },
    handleClose() {
      if (this.paymentStatus) {
        this.$emit('close');
      } else if (!this.loading) {
        if (confirm('Are you sure you want to cancel your purchase?')) {
          this.$emit('close');
        }
      }
    },
    resetPayment() {
      this.error = null;
      this.paymentStatus = null;
      this.loading = false;
      
      // Unmount and clear the existing card element
      if (this.card) {
        this.card.unmount();
        this.card = null;
      }
      
      // Reinitialize the card element
      this.$nextTick(() => {
        this.initializeStripe();
      });
    },
    async handleSubmit() {
      this.loading = true;
      this.error = null;
      
      if (!this.email) {
        this.error = 'Please enter your email address';
        this.loading = false;
        return;
      }
      
      try {
        console.log('Creating new payment method...');
        
        // Make sure we have a valid card element
        if (!this.card) {
          console.error('Card element is missing');
          throw new Error('Payment form is not ready. Please refresh and try again.');
        }
        
        const { error, paymentMethod } = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.card,
          billing_details: {
            email: this.email
          }
        });

        if (error) {
          console.error('Error creating payment method:', error);
          this.error = error.message;
          this.paymentStatus = 'failed';
          this.loading = false;
          return;
        }

        console.log('Payment method created:', paymentMethod.id);

        const response = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            paymentMethodId: paymentMethod.id,
            amount: this.price, // Use the computed price property
            email: this.email
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          console.error('Payment API error:', errorData);
          throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('Payment intent result:', result);

        if (result.error) {
          this.error = result.error;
          this.paymentStatus = 'failed';
        } else if (result.status === 'succeeded') {
          this.paymentStatus = 'success';
          this.error = null;
        } else {
          this.error = 'Payment failed. Please try again.';
          this.paymentStatus = 'failed';
        }
      } catch (err) {
        console.error('Payment error:', err);
        this.error = err.message || 'An error occurred. Please try again.';
        this.paymentStatus = 'failed';
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    show: {
      immediate: true,
      async handler(isVisible) {
        if (isVisible && !this.stripe) {
          await this.initializeStripe();
        }
      }
    }
  },
  beforeUnmount() {
    if (this.card) {
      this.card.unmount();
      this.card = null;
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
}

.order-summary {
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.product-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.product-icon {
  font-size: 1.5rem;
  color: #007bff;
}

.features-list {
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgba(0, 123, 255, 0.05);
  border-radius: 4px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.feature-item:last-child {
  margin-bottom: 0;
}

.feature-item i {
  color: #28a745;
}

.product-details {
  display: flex;
  justify-content: space-between;
  margin: 1rem 0;
  align-items: center;
}

.product-details i {
  margin-right: 0.5rem;
  color: #6c757d;
}

.total {
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.total i {
  margin-right: 0.5rem;
  color: #28a745;
}

.payment-form {
  margin-top: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
}

.form-input:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
}

.form-help {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #6c757d;
}

#card-element {
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: white;
}

.error-message {
  color: #dc3545;
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.pay-button {
  width: 100%;
  padding: 1rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  margin-top: 1.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.pay-button:hover {
  background: #0056b3;
}

.pay-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Success State Styles */
.success-state {
  text-align: center;
  padding: 2rem 0;
}

.credit-note {
  background: #fff8e1;
  border: 1px solid #FFD23F;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin: 1rem 0;
  font-size: 0.9rem;
  color: #495057;
  text-align: left;
}

.credit-note a {
  color: #007bff;
  font-weight: 600;
}

.success-icon {
  width: 64px;
  height: 64px;
  background: #28a745;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto 1rem;
}

/* Failed State Styles */
.failed-state {
  text-align: center;
  padding: 2rem 0;
}

.failed-icon {
  width: 64px;
  height: 64px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto 1rem;
}

.button-group {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
}

.secondary-button {
  padding: 1rem 2rem;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.secondary-button:hover {
  background: #5a6268;
}

.cta-button {
  padding: 1rem 2rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-align: center;
  display: inline-block;
  line-height: 1.5;
}

.cta-button:hover {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.cta-button.download-link {
  text-decoration: none;
  margin-top: 1.5rem;
  padding: 1.25rem 2.5rem;
  font-size: 1.1rem;
  letter-spacing: 0.3px;
  width: auto;
  min-width: 280px;
}

.cta-button.download-link:active {
  transform: translateY(1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Loading Spinner */
.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255,255,255,.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.form-group label i {
  margin-right: 0.5rem;
  color: #007bff;
}

.form-help i {
  margin-right: 0.25rem;
  color: #6c757d;
}

.success-icon i,
.failed-icon i {
  font-size: 2rem;
}

.cta-button i {
  margin-right: 0.5rem;
}

.cta-button.download-link i {
  font-size: 1.2rem;
}

.error-message i {
  margin-right: 0.5rem;
}

/* Enhance existing button styles */
.pay-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.pay-button i {
  font-size: 1.1rem;
}

/* Add subtle hover animations */
.feature-item {
  transition: transform 0.2s ease;
}

.feature-item:hover {
  transform: translateX(5px);
}

.product-icon {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Stripe logo styling */
.stripe-powered {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.stripe-powered img {
  height: 24px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.stripe-powered img:hover {
  opacity: 1;
}

.success-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
}

.download-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #FFD23F;
  color: #1a1a1a;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  min-width: 200px;
  justify-content: center;
}

.download-link:hover {
  background: #FFD23F;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.download-link i {
  font-size: 1.2rem;
}
</style>